import React, { useEffect, useRef, useState } from "react";
import { Box } from "rizzui/box";
import First from "./first";
import Second from "./second";
import Third from "./third";
import cn from "../../../lib/utils/class-name";
import Fourth from "./fourth";
import Ring from "../../../assets/ring";
import Bar from "../../../assets/bar";
import { Text } from "rizzui/typography";
import { ThemeSwitcher } from "../../../component/themeSwitch";
import ProjectCreated from "./success";
import RingConnect from "../../../assets/ringconnect";
import { ActionIcon } from "rizzui/action-icon";
import CloseIcon from "../../../assets/close";
import { useNavigate } from "react-router";
import CryptoJS from "crypto-js";
import PopUp from "../../../component/popUp";
import { useParams } from "react-router";
import { useSingleCampaign } from "../../../hooks/useCampaign";
import MyLoader from "../../../component/loader";
import PreviewPage from "./preview";
import { useLocation } from "react-router";

export default function NewProjects() {
  const [page, setPage] = useState(0);
  const [done, setDone] = useState(false);
  const effectRef = useRef(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const [currentId, setCurrentId] = useState<any>(id);

  useEffect(() => {
    const storedId = sessionStorage.getItem("campaign_id");
    const value = id ?? storedId;
    setCurrentId(value);
  }, [page]);

  const {
    data: campaign,
    error: fetchError,
    isLoading,
    refetch,
  } = useSingleCampaign({ id: currentId });

  fetchError && navigate("/campaigns");

  useEffect(() => {
    refetch();
  }, [currentId]);

  useEffect(() => {
    const key = process.env.REACT_APP_SECRET_KEY;
    document.title = id ? "Edit Campaign" : "New Campaign";
    let currentPage = sessionStorage.getItem("new_campaign_page");

    if (currentPage && effectRef.current === false) {
      const decryptPage = CryptoJS.AES.decrypt(currentPage, key!).toString(
        CryptoJS.enc.Utf8
      );
      if (!decryptPage) sessionStorage.removeItem("new_campaign_page");
      const pageInt = parseInt(decryptPage);
      if (pageInt <= 4) setPage(pageInt);
      else {
        sessionStorage.removeItem("new_campaign_page");
      }
    }
    effectRef.current = true;
  }, []);

  const conditionalComponent = () => {
    switch (page) {
      case 0:
        return (
          <First
            key={"firstPage"}
            data={{
              page,
              setPage,
              campaign,
            }}
          />
        );
      case 1:
        return (
          <Second
            key={"secondPage"}
            data={{
              page,
              setPage,
              campaign,
              setCurrentId,
            }}
          />
        );
      case 2:
        return (
          <Third
            key={"thirdPage"}
            data={{
              page,
              setPage,
              campaign,
              setCurrentId,
            }}
          />
        );
      case 3:
        return (
          <Fourth
            key={"fourthPage"}
            data={{
              page,
              setPage,
              setDone,
              campaign,
              setCurrentId,
              isEdit: id ? true : false,
            }}
          />
        );
      case 4:
        return (
          <PreviewPage
            key={"fourthPage"}
            data={{
              page,
              setPage,
              setDone,
              campaign,
              currentId,
              setCurrentId,
              isEdit: id ? true : false,
            }}
          />
        );
      default:
        return (
          <First
            key={"firstPage"}
            data={{
              page,
              setPage,
              campaign,
            }}
          />
        );
    }
  };

  const links = [
    { title: "Campaign details", subTitle: "Basic info" },
    { title: "AI agent", subTitle: "Details about the AI agent" },
    { title: "Call details", subTitle: "Details to train the AI" },
    { title: "Call availability", subTitle: "Set your work days and hours" },
    { title: "Test campaign", subTitle: "Review and test your campaign" },
  ];

  return (
    <>
      {isLoading ? (
        <div className="h-screen">
          <MyLoader />
        </div>
      ) : !done ? (
        <>
          <PopUp />
          <div className="fixed z-[555] right-0 top-0">
            <ThemeSwitcher />
          </div>
          <Box className="flex flex-row h-[70px] xl:justify-start justify-center w-full">
            <Box className="w-[198px] flex-col gap-[4px] ml-[150px] mt-[13px] hidden xl:flex">
              <Text className="font-ibmPlexSerif text-[18px] leading-[23.8px] font-[600] text-primary-dark">
                {id ? "Edit campaign" : "Create campaign"}
              </Text>
              <Text className="font-switzer text-[14px] leading-[18.48px] font-[400] text-[#758599]">
                Follow these simple steps
              </Text>
            </Box>
            <div className="absolute w-full justify-center flex">
              <RingConnect className={"w-[165px] h-[23px] mt-[23.58px]"} />
            </div>
            <div className="absolute right-[48px] top-[10px]">
              <ActionIcon
                variant="text"
                className="bg-[#D1D6DD] size-[28px] rounded-full"
                onClick={() => {
                  sessionStorage.removeItem("campaign_id");
                  sessionStorage.removeItem("new_campaign_page");
                  sessionStorage.removeItem("call_time");
                  navigate("/campaigns");
                }}
              >
                <CloseIcon className={"size-[10px]"} />
              </ActionIcon>
            </div>
          </Box>
          <div className="xl:mt-[57px] mt-[30px] w-full h-fit bg-background flex justify-center xl:justify-start">
            <Box className="hidden xl:flex z-[5] ml-[150px] gap-[16px] w-[215px] h-[238px]">
              <div>
                {links.map((link, i) => {
                  const isLast = links.length === i + 1;
                  const fill = page >= i ? "text-[#1FB6FF]" : "text-[#D9D9D9]";
                  return (
                    <div
                      key={link.title}
                      className={cn("h-fit place-items-center -mb-[8px]", fill)}
                    >
                      <Ring className={""} key={link.subTitle} />
                      {!isLast && <Bar className={"mt-[0px]"} />}
                    </div>
                  );
                })}
              </div>
              <div>
                {links.map((link, i) => {
                  return (
                    <div className="flex flex-col gap-[4px] mb-[28px] mt-[0px]">
                      <Text
                        key={link.title}
                        className="font-ibmPlexSerif text-[14px] leading-[18px] font-[500] text-primary-dark"
                      >
                        {link.title}
                      </Text>
                      <Text className="font-inter text-[12px] leading-[18px] font-[400] text-[#758599] -tracking-[0.01em]">
                        {link.subTitle}
                      </Text>
                    </div>
                  );
                })}
              </div>
            </Box>
            <Box
              className={cn(
                "flex xl:ml-[193px] justify-center h-fit md:w-auto w-[96%]"
              )}
            >
              {conditionalComponent()}
            </Box>
          </div>
        </>
      ) : (
        <ProjectCreated id={currentId} />
      )}
    </>
  );
}
